import { DateService } from "@/core/services/Date.service";
import { isSignedUrlExpired } from "@/core/utils";
import { DateAdapter } from "@/infrastructure/adapters/dayJs";

interface CreditDocumentProps {
  id: number;
  fileName: string;
  filePath: string;
  fileSize: number;
  createdAt: string;
  updatedAt: string;
  documentType: string;
  documentTypeKey: string;
  fileMimeType: string;
  fileDisplayName: string;
  deletable: boolean;
  signedUrl?: string;
}

export type { CreditDocumentProps };

export class CreditDocument implements CreditDocumentProps {
  private readonly _id: number;
  private readonly _fileName: string;
  private readonly _filePath: string;
  private readonly _fileSize: number;
  private readonly _createdAt: string;
  private readonly _updatedAt: string;
  private readonly _documentType: string;
  private readonly _documentTypeKey: string;
  private readonly _fileMimeType: string;
  private readonly _fileDisplayName: string;
  private readonly _deletable: boolean;
  private _signedUrl: string;

  constructor(props: CreditDocumentProps) {
    this._id = props.id;
    this._fileName = props.fileName;
    this._filePath = props.filePath;
    this._fileSize = props.fileSize;
    this._createdAt = props.createdAt;
    this._updatedAt = props.updatedAt;
    this._documentType = props.documentType;
    this._documentTypeKey = props.documentTypeKey;
    this._fileMimeType = props.fileMimeType;
    this._fileDisplayName = props.fileDisplayName;
    this._deletable = props.deletable;
    this._signedUrl = props.signedUrl ?? "";
  }

  public get id(): number {
    return this._id;
  }

  public get fileName(): string {
    return this._fileName;
  }

  public get filePath(): string {
    return this._filePath;
  }

  public get fileSize(): number {
    return this._fileSize;
  }

  public get createdAt(): string {
    return this._createdAt;
  }

  public get updatedAt(): string {
    return this._updatedAt;
  }

  public createdAtFormatted(format: string): string {
    return new DateService(new DateAdapter()).format(this._createdAt, format);
  }

  public get documentType(): string {
    return this._documentType;
  }

  public get documentTypeKey(): string {
    return this._documentTypeKey;
  }

  public get fileMimeType(): string {
    return this._fileMimeType;
  }

  public get fileDisplayName(): string {
    return this._fileDisplayName;
  }

  public get deletable(): boolean {
    return this._deletable;
  }

  public set signedUrl(signedUrl: string) {
    this._signedUrl = signedUrl;
  }

  public get signedUrl(): string {
    return this._signedUrl;
  }

  public get isExpired(): boolean {
    return isSignedUrlExpired(this._signedUrl);
  }

  public static fromJson(json: CreditDocumentProps): CreditDocument {
    return new CreditDocument(json);
  }
}
